/**
 * Every URL from `import.meta.env.VITE_*` — **no hardcoded origin anywhere**.
 *
 * These are compiled into the bundle, so they are public-safe values only
 * (APP_DEVELOPMENT_GUIDE §10). A secret here ships to every browser.
 *
 * Deployed, the API has its own hostname (FiveOX ADR-0056):
 * `api.uat.fiveox.ai` and `api.fiveox.ai` serve `/api/*`, and
 * `accounts.<env>` keeps the sign-in surface and serves no `/api/` route.
 * **Local development is unchanged** — `localhost:8100` still serves both.
 */

const env = import.meta.env

function required(name: string, value: string | undefined, fallback: string): string {
  if (value) return value
  if (env.PROD) {
    // A production bundle built without its config is the failure that looks
    // like a backend outage. Say so at boot, loudly, rather than 404ing later.
    console.error(`[prodoc] ${name} is not set; falling back to ${fallback}`)
  }
  return fallback
}

const EDGE = 'http://localhost:8100'

export const AUTH_API = required('VITE_AUTH_API', env.VITE_AUTH_API, `${EDGE}/api/auth`)
export const ACCESS_API = required('VITE_ACCESS_API', env.VITE_ACCESS_API, `${EDGE}/api/access`)
export const APPMGMT_API = required('VITE_APPMGMT_API', env.VITE_APPMGMT_API, `${EDGE}/api/apps`)
export const COMM_API = required('VITE_COMM_API', env.VITE_COMM_API, `${EDGE}/api/comm`)
export const USERS_API = required('VITE_USERS_API', env.VITE_USERS_API, `${EDGE}/api/users`)
export const ORGS_API = required('VITE_ORGS_API', env.VITE_ORGS_API, `${EDGE}/api/orgs`)

/**
 * ProDoc's own service, reached **directly** — 8170 locally, allocated not
 * chosen (ADR-0003). Routes are `/api/v1/*` on the service; the prototype's
 * `/api/prodoc/v1` was the edge's slug prefix, which a deployed vhost adds and
 * the browser never spells.
 */
export const SERVICE_API = required('VITE_PRODOC_API', env.VITE_PRODOC_API, 'http://localhost:8170')

export const ACCOUNTS_URL = required('VITE_ACCOUNTS_URL', env.VITE_ACCOUNTS_URL, 'http://localhost:3102')
export const LAUNCHER_URL = required('VITE_LAUNCHER_URL', env.VITE_LAUNCHER_URL, 'http://localhost:3104')
export const CONSOLE_URL = required('VITE_CONSOLE_URL', env.VITE_CONSOLE_URL, 'http://localhost:3106')
export const STORE_URL = required('VITE_STORE_URL', env.VITE_STORE_URL, 'http://localhost:3107')
export const HELP_URL = required('VITE_HELP_URL', env.VITE_HELP_URL, 'http://localhost:3108')

/**
 * Staff surfaces, for the waffle's **internal** mode (ADR-0054). Keyed by
 * *registry* slug (`core-admin`), not app slug — that is what
 * `GET /api/access/staff/me/access` answers with.
 *
 * An external user never sees these; the resolver only asks for them when the
 * token says `user_type: internal`, and an entry with no URL is dropped rather
 * than rendered dead.
 */
export const ADMIN_URL = env.VITE_ADMIN_URL ?? 'http://localhost:3103'
export const CRM_URL = env.VITE_CRM_URL ?? 'http://localhost:3105'
export const SUPPORT_URL = env.VITE_SUPPORT_URL ?? 'http://localhost:3109'

/** The registered OAuth `client_id` (APP_DEVELOPMENT_GUIDE §6, registration 1; fiveox wave P). */
export const SSO_CLIENT_ID = env.VITE_SSO_CLIENT_ID ?? 'prodoc'

/** The API-stable slug, in ONE place (FiveOX ADR-0011; the catalog has carried `prodoc` since the seed). */
export const APP_SLUG = env.VITE_APP_SLUG ?? 'prodoc'
export const APP_NAME = 'ProDoc'

/**
 * The origin allow-list, compared as a **parsed origin**, never as a string
 * prefix (FiveOX ADR-0048). `https://evil.com/?x=https://fiveox.ai` has
 * the right prefix and is not the right origin.
 */
export const PLATFORM_ORIGINS: string[] = String(env.VITE_PLATFORM_ORIGINS ?? '')
  .split(',')
  .map((origin: string) => origin.trim())
  .filter(Boolean)

export function isAllowedOrigin(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin === window.location.origin) return true
    return PLATFORM_ORIGINS.some((allowed: string) => {
      try {
        return new URL(allowed).origin === parsed.origin
      } catch {
        return false
      }
    })
  } catch {
    return false
  }
}
